import React, { useEffect, useState } from 'react'
import useFetch from 'use-http'

import { Box, Button, Card, Heading, Inline, Modal, Stack, Text, TextField, Toggle } from '@myxplor/stardust'

interface Props {
  isOpen: boolean
  user: Nullable<Papi.User>
  vendors: Papi.Vendor[]
  onCancel(): void
  onSave(user: Papi.User): void
}

const ServiceModal = ({ isOpen, user, vendors, onCancel, onSave }: Props) => {
  const [userModel, setUserModel] = useState<Nullable<Record<string, any>>>(null)

  const userRequest = useFetch('/api/v1/users')

  const userVendorIds = (userModel?.user_vendors || []).map(
    (userVendor: Papi.UserVendor) => userVendor.vendor_id
  )

  const onChange = (field: string, value: any) => {
    setUserModel((model: any) => ({ ...model, [field]: value }))
  }

  // Gross
  const onVendorToggle = (vendorId: number) => {
    setUserModel((model) => {
      if (model === null) return null
      const userVendors = (model.user_vendors || []) as Record<string, any>[]
      const deltaUserVendors = userVendors.find((userVendor) => userVendor.vendor_id === vendorId)
        ? userVendors.filter((userVendor) => userVendor.vendor_id !== vendorId)
        : [...userVendors, { vendor_id: vendorId }]

      return { ...model, user_vendors: deltaUserVendors }
    })
  }

  const handleSave = async () => {
    if (userModel) {
      const response = user?.id
        ? await userRequest.patch(`/${user.id}`, userModel)
        : await userRequest.post(userModel)

      onSave(response.data)
    }
  }

  useEffect(() => setUserModel(user || {}), [isOpen, user])

  if (!userModel) {
    return null
  }

  return (
    <Modal backdrop isOpen={isOpen} onBlur={onCancel}>
      <Card>
        <Box minWidth={600} padding="small">
          <Stack space="small">
            <Box paddingBottom="small">
              <Heading level={5}>User</Heading>
            </Box>
            <TextField
              label="Display name"
              name="display_name"
              placeholder="Display name for this service within Starmap"
              required
              value={userModel.display_name || ''}
              onChange={(value) => onChange('display_name', value)}
            />
            <TextField
              label="Identifier"
              name="identifier"
              placeholder="The username the user will use to login"
              required
              value={userModel.identifier || ''}
              onChange={(value) => onChange('identifier', value)}
            />
            <TextField
              autoComplete="off"
              label="Password"
              name="password"
              placeholder="The password the user will use to login"
              required
              value={userModel.password || ''}
              onChange={(value) => onChange('password', value)}
            />
            <Inline alignY="center">
              <Toggle on={userModel.admin} onChange={() => onChange('admin', !userModel.admin)} />
              <Text>Admin</Text>
            </Inline>
            <Stack space="none">
              <Text weight="bold">Vendors</Text>
              {vendors.map((vendor, i) => (
                <Inline alignY="center" key={`${i}`}>
                  <Toggle on={userVendorIds.includes(vendor.id)} onChange={() => onVendorToggle(vendor.id)} />
                  <Text>{vendor.name}</Text>
                </Inline>
              ))}
            </Stack>
            <Inline align="right" space="small">
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save</Button>
            </Inline>
          </Stack>
        </Box>
      </Card>
    </Modal>
  )
}

export default React.memo(ServiceModal)
